import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppState } from "../context/AppState";
import { api, authenticatedApi } from "../lib/api";
import { useLocale } from "../context/Locale";
import ProductCustomizer from "../components/ProductCustomizer";
import ThemeLoader, { cacheThemeLoader, readCachedThemeLoader } from "../components/ThemeLoader";

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function plainText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function categoryIcon(name) {
  const value = String(name || "").toLocaleLowerCase("es");
  if (/(combo|promoci|oferta)/.test(value)) return "🍿";
  if (/(caja|box|familiar|familia)/.test(value)) return "📦";
  if (/(bebida|gaseosa|jugo|refresco)/.test(value)) return "🥤";
  if (/(pollo|broaster|parrilla)/.test(value)) return "🍗";
  if (/(hamburg|sandwich)/.test(value)) return "🍔";
  if (/(pizza)/.test(value)) return "🍕";
  if (/(postre|dulce|helado)/.test(value)) return "🍰";
  return "🍽️";
}

function businessNameFromSlug(slug) {
  const parts = decodeURIComponent(String(slug || ""))
    .replace(/[^a-z0-9áéíóúüñ-]+/gi, "-")
    .split("-")
    .filter(Boolean);

  const lastPart = parts[parts.length - 1] || "";
  if (parts.length > 1 && lastPart.length >= 10 && /\d/.test(lastPart)) {
    parts.pop();
  }

  const name = parts
    .map((part) => part.charAt(0).toLocaleUpperCase("es") + part.slice(1).toLocaleLowerCase("es"))
    .join(" ")
    .trim();

  return name || "este negocio";
}

function ProductLabels({ item, t }) {
  if (!Number(item?.is_recommended) && !Number(item?.is_popular) && !Number(item?.is_new)) return null;
  return (
    <div className="product-labels" aria-label="Características del producto">
      {Number(item.is_recommended) === 1 && <span className="is-recommended">★ {t("recommendedLabel")}</span>}
      {Number(item.is_popular) === 1 && <span className="is-popular">🔥 {t("popularLabel")}</span>}
      {Number(item.is_new) === 1 && <span className="is-new">{t("newLabel")}</span>}
    </div>
  );
}

export default function StorePage() {
  const { slug } = useParams();
  const requestedBusinessName = useMemo(() => businessNameFromSlug(slug), [slug]);
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, cart, changeCartQuantity, user } = useAppState();
  const { t } = useLocale();
  const [restaurant, setRestaurant] = useState(null);
  const [groups, setGroups] = useState({});
  const [categories, setCategories] = useState({});
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themeLoader, setThemeLoader] = useState(() => readCachedThemeLoader());
  const [error, setError] = useState("");
  const [provisionalBusiness, setProvisionalBusiness] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [customizingItemId, setCustomizingItemId] = useState(null);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [orderBlockedNotice, setOrderBlockedNotice] = useState("");
  const [originalSearchOpen, setOriginalSearchOpen] = useState(false);
  const storeAcceptsOrders = Number(restaurant?.is_active) === 1
    && Number(restaurant?.is_accepted ?? 1) === 1
    && Number(restaurant?.commission_blocked || 0) !== 1;

  useEffect(() => {
    let active = true;
    api("/service-hub")
      .then((data) => {
        if (!active) return;
        const theme = String(data?.food_home?.theme || "current");
        const nextLoader = String(data?.food_home?.loaders?.[theme] || "");
        setThemeLoader(nextLoader);
        cacheThemeLoader(nextLoader);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const showAdded = (event) => {
      setAddedFeedback({
        itemId: Number(event.detail?.itemId),
        name: event.detail?.name || "Producto",
        nonce: Date.now(),
      });
    };
    window.addEventListener("dh:cart-added", showAdded);
    return () => window.removeEventListener("dh:cart-added", showAdded);
  }, []);

  useEffect(() => {
    if (!addedFeedback) return undefined;
    const timer = window.setTimeout(() => setAddedFeedback(null), 1300);
    return () => window.clearTimeout(timer);
  }, [addedFeedback]);

  useEffect(() => {
    if (!previewItem) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPreviewItem(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [previewItem]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setProvisionalBusiness(null);

    Promise.all([
      user?.auth_token
        ? authenticatedApi(`/get-restaurant-info-with-favourite/${slug}`, user.auth_token, {
            method: "POST",
            body: "{}",
          })
        : api(`/get-restaurant-info/${slug}`, { method: "POST", body: "{}" }),
      api(`/get-restaurant-items/${slug}`, { method: "POST", body: "{}" }),
    ])
      .then(([info, catalog]) => {
        if (!active) return;
        setRestaurant(info);
        setGroups(catalog?.items || {});
        setCategories(catalog?.categories || {});
        setRecommended(Array.isArray(catalog?.recommended) ? catalog.recommended : []);
      })
      .catch(async (requestError) => {
        try {
          const provisional = await api(`/provisional-businesses/${slug}`);
          if (!active) return;
          if (provisional?.redirect_slug) {
            navigate(`/food/stores/${provisional.redirect_slug}`, { replace: true });
            return;
          }
          setProvisionalBusiness(provisional);
          setError("");
        } catch {
          if (active) setError(requestError.message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [navigate, slug, user?.auth_token]);

  async function toggleFavorite() {
    if (!user?.auth_token) return;
    setFavoriteBusy(true);
    try {
      const updated = await authenticatedApi("/toggle-favorite", user.auth_token, {
        method: "POST",
        body: JSON.stringify({ id: restaurant.id }),
      });
      setRestaurant((current) => ({
        ...current,
        is_favorited: updated.is_favorited,
        followers_count: updated.followers_count,
      }));
    } finally {
      setFavoriteBusy(false);
    }
  }

  function shareStore() {
    const shareUrl = new URL(window.location.href);
    shareUrl.search = "";
    shareUrl.hash = "";
    shareUrl.searchParams.set("shared", String(Date.now()));
    const shareData = {
      title: restaurant.name,
      text: restaurant.description,
      url: shareUrl.toString(),
    };
    if (navigator.share) navigator.share(shareData).catch(() => {});
    else navigator.clipboard?.writeText(shareUrl.toString()).catch(() => {});
  }

  function itemLines(itemId) {
    return cart.filter(
      (line) =>
        Number(line.itemId) === Number(itemId)
        && String(line.restaurantSlug) === String(slug),
    );
  }

  function addItem(item) {
    if (!storeAcceptsOrders) {
      setOrderBlockedNotice("Este negocio está cerrado y no acepta pedidos en este momento.");
      return;
    }
    if ((item.addon_categories || []).length > 0) {
      setCustomizingItemId(item.id);
      return;
    }
    addToCart(item, restaurant, [], 1);
  }

  function removeItem(item) {
    const lines = itemLines(item.id);
    const line = lines[lines.length - 1];
    if (line) changeCartQuantity(line.lineId, line.quantity - 1);
  }

  function openItemPreview(item) {
    if (!item.image) return;
    setPreviewZoom(1);
    setPreviewItem(item);
  }

  function closeItemPreview() {
    setPreviewItem(null);
    setPreviewZoom(1);
  }

  function addPreviewItem() {
    const item = previewItem;
    closeItemPreview();
    if (item) addItem(item);
  }

  const visibleGroups = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("es");
    if (!search) return Object.entries(groups);
    return Object.entries(groups)
      .map(([category, items]) => [
        category,
        items.filter((item) =>
          `${item.name} ${item.desc || ""}`.toLocaleLowerCase("es").includes(search),
        ),
      ])
      .filter(([, items]) => items.length);
  }, [groups, query]);

  const storeCart = useMemo(
    () => cart.filter((line) => String(line.restaurantSlug) === String(slug)),
    [cart, slug],
  );
  const storeCartCount = useMemo(
    () => storeCart.reduce((sum, line) => sum + Number(line.quantity || 0), 0),
    [storeCart],
  );
  const storeCartTotal = useMemo(
    () => storeCart.reduce((sum, line) => {
      const addons = (line.selectedAddons || []).reduce(
        (addonSum, addon) => addonSum + Number(addon.price || 0),
        0,
      );
      return sum + (Number(line.price || 0) + addons) * Number(line.quantity || 0);
    }, 0),
    [storeCart],
  );

  useEffect(() => {
    const firstCategory = Object.keys(groups)[0] || "";
    setActiveCategory((current) => (
      current && Object.prototype.hasOwnProperty.call(groups, current)
        ? current
        : firstCategory
    ));
  }, [groups]);

  useEffect(() => {
    const itemId = Number(new URLSearchParams(location.search).get("item"));
    if (!Number.isFinite(itemId) || itemId <= 0) return;

    for (const [category, items] of Object.entries(groups)) {
      const item = items.find((entry) => Number(entry.id) === itemId);
      if (item) {
        setActiveCategory(category);
        setPreviewZoom(1);
        setPreviewItem(item);
        return;
      }
    }
  }, [groups, location.search]);

  if (loading) {
    return <ThemeLoader src={themeLoader} label={t("loadingMenu")} />;
  }

  if (error || !restaurant) {
    const missingBusinessName = provisionalBusiness?.name || requestedBusinessName;
    return (
      <main className="store-page store-missing">
        <section className="store-missing__card">
          <span className="store-missing__eyebrow">NEGOCIO AÚN NO DISPONIBLE</span>
          <div className="store-missing__icon" aria-hidden="true">🏪</div>
          <h1>¿Buscabas <strong>{missingBusinessName}</strong>?</h1>
          {(provisionalBusiness?.category || provisionalBusiness?.address) && (
            <div className="store-missing__business-meta">
              {provisionalBusiness.category && <b>{provisionalBusiness.category}</b>}
              {provisionalBusiness.address && <span>⌖ {provisionalBusiness.address}</span>}
            </div>
          )}
          <p>
            Todavía no encontramos su carta en Delivery Huaraz. Mientras lo incorporamos,
            puedes descubrir otros negocios que ya reciben pedidos cerca de ti.
          </p>

          <div className="store-missing__actions">
            <Link className="store-missing__primary" to="/food">Ver negocios disponibles</Link>
            <Link className="store-missing__secondary" to="/explore">Buscar otro negocio</Link>
          </div>

          <aside className="store-missing__owner">
            <div>
              <span>¿ERES EL DUEÑO DE {missingBusinessName.toLocaleUpperCase("es")}?</span>
              <h2>Agrega tu negocio a Delivery Huaraz</h2>
              <p>Publica tu carta, recibe pedidos en línea y llega a más clientes de Huaraz.</p>
            </div>
            <a href="/public/auth/store-registration">Agregar mi negocio <span aria-hidden="true">→</span></a>
          </aside>
        </section>
      </main>
    );
  }

  const storefrontTheme = ["immersive", "classic", "compact", "original"].includes(restaurant.storefront_theme)
    ? restaurant.storefront_theme
    : "classic";
  const heroImage = restaurant.cover_image || restaurant.image;
  const originalDeliveryFee = Number(
    restaurant.delivery_charge_type === "DYNAMIC"
      ? restaurant.base_delivery_charge
      : restaurant.delivery_charges,
  );
  const originalPickupMinutes = Math.max(10, Number(restaurant.delivery_time || 20));
  const storeRating = Math.max(0, Math.min(5, Number(restaurant.avgRating || restaurant.rating || 0)));
  const displayedGroups = storefrontTheme === "compact" && !query.trim()
    ? visibleGroups.filter(([category]) => category === activeCategory)
    : visibleGroups;

  return (
    <main className={`store-page store-theme-${storefrontTheme} ${originalSearchOpen ? "is-original-search-open" : ""}`.trim()}>
      <header className="store-hero" style={{ backgroundImage: `url("${heroImage}")` }}>
        <div className="store-hero__veil" />
        <nav className="store-toolbar" aria-label={t("storeActions")}>
          <Link to="/food" aria-label={t("backStores")}>‹</Link>
          <Link className="store-toolbar__brand" to="/inicio" aria-label="Delivery Huaraz">
            <span aria-hidden="true">⌁</span> Delivery Huaraz
          </Link>
          {storefrontTheme === "original" ? (
            <button
              className="store-toolbar__search"
              type="button"
              aria-label={t("searchProducts")}
              onClick={() => {
                setOriginalSearchOpen((current) => !current);
                window.setTimeout(() => document.querySelector(".store-search input")?.focus(), 80);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="10.8" cy="10.8" r="6.4" />
                <path d="m15.5 15.5 4.5 4.5" />
              </svg>
            </button>
          ) : (
            <>
              <button type="button" aria-label={t("shareStore")} onClick={shareStore}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="18" cy="5" r="2.5" />
                  <circle cx="6" cy="12" r="2.5" />
                  <circle cx="18" cy="19" r="2.5" />
                  <path d="m8.2 10.8 7.5-4.4M8.2 13.2l7.5 4.4" />
                </svg>
              </button>
              {user ? (
                <button
                  type="button"
                  aria-label={restaurant.is_favorited ? "Quitar de favoritos" : "Agregar a favoritos"}
                  className={restaurant.is_favorited ? "is-favorite" : ""}
                  disabled={favoriteBusy}
                  onClick={toggleFavorite}
                >
                  <span aria-hidden="true">{restaurant.is_favorited ? "♥" : "♡"}</span>
                </button>
              ) : (
                <Link className="store-toolbar__favorite" to={`/account?next=/food/stores/${slug}`} aria-label="Inicia sesión para guardar">♡</Link>
              )}
            </>
          )}
        </nav>

        <section className="store-hero__content">
          <img className="store-hero__logo" src={restaurant.image} alt={restaurant.name} />
          <h1>{restaurant.name}</h1>
          <span className={`store-open-status ${Number(restaurant.is_active) === 1 ? "is-open" : "is-closed"}`}>
            <i aria-hidden="true" /> {Number(restaurant.is_active) === 1 ? t("openNow") : t("closedNow")}
          </span>
          <p>{restaurant.description}</p>
          <div className="store-hero__meta">
            <span>◷ {restaurant.delivery_time || "20"} min</span>
            <span>♧ Delivery</span>
            {storefrontTheme === "original" ? (
              <Link className="original-rating-link" to={`/reviews/${slug}`} aria-label={`Calificación ${storeRating.toFixed(1)} de 5`}>
                <span className="original-rating-stars" aria-hidden="true">
                  <i>★</i>
                </span>
                <b>{storeRating.toFixed(1)}</b>
              </Link>
            ) : (
              <Link to={`/reviews/${slug}`}>★ {restaurant.avgRating || restaurant.rating || "0"}</Link>
            )}
          </div>
          {storefrontTheme === "original" ? (
            <>
              <Link className="original-info-link" to={`/reviews/${slug}`} aria-label="Información del negocio">
                <span aria-hidden="true">ⓘ</span>
              </Link>
              <div className="original-action-row">
                <span className="original-time-pill">♨ <b>{restaurant.delivery_time || "20"} min</b></span>
                <span className={`original-fulfillment ${Number(restaurant.delivery_type) === 3 ? "" : "is-delivery-only"}`.trim()}>
                  <span>
                    <b>Delivery</b>
                    <strong>{originalDeliveryFee > 0 ? money(originalDeliveryFee) : "Según zona"}</strong>
                  </span>
                  {Number(restaurant.delivery_type) === 3 && (
                    <span>
                      <b>Recojo</b>
                      <strong>{originalPickupMinutes}-{originalPickupMinutes + 10} min</strong>
                    </span>
                  )}
                </span>
                <span className="original-follow-chip">
                  {user ? (
                    <button
                      aria-label={restaurant.is_favorited ? "Dejar de seguir" : "Seguir negocio"}
                      className={restaurant.is_favorited ? "is-favorite" : ""}
                      disabled={favoriteBusy}
                      onClick={toggleFavorite}
                      type="button"
                    >
                      {restaurant.is_favorited ? "✓ Siguiendo" : "Seguir"}
                    </button>
                  ) : (
                    <Link aria-label="Inicia sesión para seguir el negocio" to={`/account?next=/food/stores/${slug}`}>Seguir</Link>
                  )}
                  <small className="store-followers">{Number(restaurant.followers_count || 0)} seguidor{Number(restaurant.followers_count || 0) === 1 ? "" : "es"}</small>
                </span>
              </div>
            </>
          ) : (
            <div className="store-hero__actions">
              {user ? (
                <button
                  aria-label={restaurant.is_favorited ? "Dejar de seguir" : "Seguir negocio"}
                  className={restaurant.is_favorited ? "is-favorite" : ""}
                  disabled={favoriteBusy}
                  onClick={toggleFavorite}
                  type="button"
                >
                  {restaurant.is_favorited ? "✓ Siguiendo" : "+ Seguir"}
                </button>
              ) : (
                <Link aria-label="Inicia sesión para seguir el negocio" to={`/account?next=/food/stores/${slug}`}>+ Seguir</Link>
              )}
              <Link className="store-hero__info" to={`/reviews/${slug}`} aria-label="Información del negocio">
                <span aria-hidden="true">ⓘ</span>
              </Link>
            </div>
          )}
          {storefrontTheme !== "original" && (
            <small className="store-followers">{Number(restaurant.followers_count || 0)} seguidor{Number(restaurant.followers_count || 0) === 1 ? "" : "es"}</small>
          )}
        </section>
      </header>

      <div className="store-catalog">
        {!storeAcceptsOrders && (
          <div className="store-order-closed" role="status">
            <strong>{t("storeClosed")}</strong>
            <span>{t("storeClosedMenuHelp")}</span>
          </div>
        )}
        {orderBlockedNotice && (
          <div className="store-order-notice" role="alert">
            <span>{orderBlockedNotice}</span>
            <button type="button" onClick={() => setOrderBlockedNotice("")} aria-label={t("closeNotice")}>×</button>
          </div>
        )}
        {storefrontTheme === "compact" && restaurant.description && (
          <p className="store-compact-description">{restaurant.description}</p>
        )}
        <label className="food-search store-search">
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="6.5" />
              <path d="m16 16 4 4" />
            </svg>
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("searchInStore").replace("{store}", restaurant.name)}
          />
        </label>

        {recommended.length > 0 && !query.trim() && (
          <section className="store-recommended" aria-label={t("recommendedProducts")}>
            <header>
              <div>
                <small>{t("recommendedEyebrow")}</small>
                <h2>{t("recommendedProducts")}</h2>
              </div>
              <span aria-hidden="true">★</span>
            </header>
            <div className="store-recommended__rail">
              {recommended.map((item) => {
                const quantity = itemLines(item.id).reduce(
                  (sum, line) => sum + Number(line.quantity || 0),
                  0,
                );
                return (
                  <article className="store-recommended__card" key={`recommended-${item.id}`}>
                    <button
                      className="store-recommended__media"
                      type="button"
                      onClick={() => openItemPreview(item)}
                      disabled={!item.image}
                      aria-label={item.image ? `Ampliar imagen de ${item.name}` : `Sin imagen para ${item.name}`}
                    >
                      {item.image ? <img src={item.image} alt={item.name} loading="lazy" /> : <span aria-hidden="true">🍽️</span>}
                    </button>
                    <div className="store-recommended__copy">
                      <ProductLabels item={item} t={t} />
                      <h3>{item.name}</h3>
                      {item.desc && <p>{plainText(item.desc)}</p>}
                      <b>{money(item.price)}</b>
                    </div>
                    <div className="store-recommended__controls">
                      {quantity > 0 ? (
                        <div className="store-recommended__stepper">
                          <button type="button" onClick={() => removeItem(item)} aria-label={`Quitar ${item.name}`}>−</button>
                          <strong>{quantity}</strong>
                          <button type="button" onClick={() => addItem(item)} disabled={!storeAcceptsOrders} aria-label={`Agregar ${item.name}`}>+</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => addItem(item)} disabled={!storeAcceptsOrders}>
                          {t("addProduct")} <span aria-hidden="true">＋</span>
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {Object.keys(groups).length > 0 && (
          <nav className="category-strip" aria-label={t("storeCategories")}>
            {Object.keys(groups).map((category) => (
              <a
                className={activeCategory === category ? "is-active" : ""}
                href={`#category-${encodeURIComponent(category)}`}
                key={category}
                onClick={(event) => {
                  if (storefrontTheme === "compact") event.preventDefault();
                  setActiveCategory(category);
                }}
              >
                {categories?.[category]?.image ? (
                  <img src={categories[category].image} alt="" loading="lazy" />
                ) : (
                  <span aria-hidden="true">{categoryIcon(category)}</span>
                )}
                <b>{category}</b>
              </a>
            ))}
          </nav>
        )}

        {displayedGroups.map(([category, items]) => (
          <section
            className="menu-category"
            id={`category-${encodeURIComponent(category)}`}
            key={category}
          >
            <h2>{category}</h2>
            <div className="menu-items">
              {items.map((item) => {
                const quantity = itemLines(item.id).reduce(
                  (sum, line) => sum + Number(line.quantity || 0),
                  0,
                );
                return (
                <article className="menu-item menu-item--published" key={item.id}>
                  <button
                    className="menu-item__media"
                    type="button"
                    onClick={() => openItemPreview(item)}
                    disabled={!item.image}
                    aria-label={item.image ? `Ampliar imagen de ${item.name}` : `Sin imagen para ${item.name}`}
                  >
                    {item.image ? (
                      <img src={item.image} alt={item.name} loading="lazy" />
                    ) : (
                      <span aria-hidden="true">🍽️</span>
                    )}
                    {item.image && <i aria-hidden="true">⌕</i>}
                  </button>
                  <div className="menu-item__copy">
                    <h3>{item.name}</h3>
                    <ProductLabels item={item} t={t} />
                    {item.desc && <p>{plainText(item.desc)}</p>}
                    <b>{money(item.price)}</b>
                  </div>
                  <div className="menu-item__controls">
                    {(item.addon_categories || []).length > 0 && <small>{t("customizable")}</small>}
                    {quantity > 0 ? (
                      <div className="menu-item__stepper">
                        <button type="button" onClick={() => removeItem(item)} aria-label={`Quitar ${item.name}`}>−</button>
                        <strong>{quantity}</strong>
                        <button
                          className={addedFeedback?.itemId === Number(item.id) ? "is-added" : ""}
                          type="button"
                          onClick={() => addItem(item)}
                          disabled={!storeAcceptsOrders}
                          aria-label={`Agregar ${item.name}`}
                        >
                          {addedFeedback?.itemId === Number(item.id) ? "✓" : "+"}
                        </button>
                      </div>
                    ) : (
                      <button
                        className={`menu-item__add ${addedFeedback?.itemId === Number(item.id) ? "is-added" : ""}`.trim()}
                        type="button"
                        onClick={() => addItem(item)}
                        disabled={!storeAcceptsOrders}
                      >
                        {addedFeedback?.itemId === Number(item.id)
                          ? `✓ ${t("added")}`
                          : storefrontTheme === "classic" ? `${t("addProduct")} +` : t("addProduct")}
                        <span aria-hidden="true">{addedFeedback?.itemId === Number(item.id) ? "" : "＋"}</span>
                      </button>
                    )}
                  </div>
                </article>
              )})}
            </div>
          </section>
        ))}

        {!displayedGroups.length && (
          <div className="food-message">
            <b>{t("noProducts")}</b>
            <span>{t("anotherWord")}</span>
          </div>
        )}
      </div>

      {storeCartCount > 0 && (
        <Link className={`cart-dock ${addedFeedback ? "is-added" : ""}`.trim()} to="/cart">
          <span className="cart-dock__label"><i aria-hidden="true">🛒</i><b>{t("viewMyOrder")}</b></span>
          <strong>{money(storeCartTotal)} <i aria-hidden="true">›</i></strong>
        </Link>
      )}
      {previewItem && (
        <div className="product-preview" role="presentation" onMouseDown={closeItemPreview}>
          <section
            className="product-preview__card"
            role="dialog"
            aria-modal="true"
            aria-label={`Vista ampliada de ${previewItem.name}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <small>{t("productDetail").toUpperCase()}</small>
                <h2>{previewItem.name}</h2>
              </div>
              <button type="button" onClick={closeItemPreview} aria-label="Cerrar imagen">×</button>
            </header>
            <div className="product-preview__viewport">
              <img
                src={previewItem.image}
                alt={previewItem.name}
                style={{ transform: `scale(${previewZoom})` }}
                onClick={() => setPreviewZoom((current) => (current > 1 ? 1 : 1.8))}
              />
            </div>
            <div className="product-preview__zoom" aria-label="Controles de zoom">
              <button
                type="button"
                onClick={() => setPreviewZoom((current) => Math.max(1, current - 0.25))}
                disabled={previewZoom <= 1}
                aria-label="Alejar imagen"
              >
                −
              </button>
              <span>{Math.round(previewZoom * 100)}%</span>
              <button
                type="button"
                onClick={() => setPreviewZoom((current) => Math.min(2.5, current + 0.25))}
                disabled={previewZoom >= 2.5}
                aria-label="Acercar imagen"
              >
                +
              </button>
            </div>
            <div className="product-preview__details">
              {previewItem.desc && <p>{plainText(previewItem.desc)}</p>}
              <b>{money(previewItem.price)}</b>
              <button type="button" onClick={addPreviewItem} disabled={!storeAcceptsOrders}>
                {(previewItem.addon_categories || []).length > 0 ? t("customizeAndAdd") : t("addToOrder")}
              </button>
            </div>
          </section>
        </div>
      )}
      {addedFeedback && (
        <div className="cart-add-feedback" role="status" aria-live="polite" key={addedFeedback.nonce}>
          <span aria-hidden="true">✓</span>
          <b>{addedFeedback.name}</b>
          <small>{t("addedToCart")}</small>
        </div>
      )}
      {customizingItemId && (
        <ProductCustomizer
          itemId={customizingItemId}
          restaurant={restaurant}
          onClose={() => setCustomizingItemId(null)}
          onConfirm={(...args) => {
            if (!storeAcceptsOrders) {
              setCustomizingItemId(null);
              setOrderBlockedNotice(t("storeNotAcceptingOrders"));
              return;
            }
            addToCart(...args);
          }}
        />
      )}
    </main>
  );
}
